import React, {ReactElement} from 'react';
import {graphql, PageProps} from 'gatsby';
import {useIntl} from 'react-intl';
import {useLocale, Locale} from '../RootProvider/LocaleContext';
import GeneralLayout from '../components/DesignSystem/Layout/GeneralLayout/GeneralLayout';
import {getPageRoute} from '../utils/routes/getPageRoute';
import {LinkProp} from '../types';
import SEO from '../components/seo';
import KnowledgeHubHero from '../components/KnowledgeHubHero/KnowledgeHubHero';
import LayoutSection from '../components/DesignSystem/Layout/LayoutSection/LayoutSection';
import {LayoutBackground} from '../components/DesignSystem/Layout/LayoutSection/LayoutSection.types';
import LookingForFinance, {LookingForFinanceSize} from '../components/DesignSystem/LookingForFinance/LookingForFinance';
import RecommendedHubs from '../components/RecommendedHubs/RecommendedHubs';
import GrayLine from '../components/DesignSystem/Layout/GrayLine/GrayLine';
import womanAtFrontDoor from '../images/womanAtFrontDoor.png';
import coupleWorking from '../images/coupleWorking.png';
import useHandleAffid from '../useHandleAffid';
import LinkColumnsWithTitle from '../components/DesignSystem/LinkColumnsWithTitle/LinkColumnsWithTitle';

export const query = graphql`
  query KnowledgePageCategory($slug: String!, $locale: String!) {
    allContentfulKnowledgePage(
      filter: {displayOnLocale: {in: [$locale]}, node_locale: {in: [$locale]}, category: {slug: {eq: $slug}}}
      sort: {fields: [createdAt], order: DESC}
    ) {
      edges {
        node {
          id
          name
          slug
        }
      }
      totalCount
    }
    contentfulKnowledgeCategories(slug: {eq: $slug}, node_locale: {eq: $locale}) {
      slug
      title
    }
  }
`;

export type KnowledgeCategoryPageData = {
  allContentfulKnowledgePage: {
    edges: [
      {
        node: {
          id: string;
          name: string;
          slug: string;
        };
      }
    ];
    totalCount: number;
  };
  contentfulKnowledgeCategories: {
    title: string;
    slug: string;
  };
};

export interface Props extends PageProps<KnowledgeCategoryPageData, {slug: string; locale: Locale}> {}

function KnowledgeCategoryPage({data}: Props): ReactElement {
  useHandleAffid();
  const locale = useLocale();
  const knowledgeArticles = data.allContentfulKnowledgePage.edges.map(({node}) => node);
  const category = data.contentfulKnowledgeCategories;
  const {formatMessage} = useIntl();

  const links: LinkProp[] = knowledgeArticles.map((article) => ({
    href: getPageRoute('knowledge.article', locale, {contentfulSlug: article.slug}),
    label: article.name,
  }));
  const hubs = [
    {
      title: formatMessage({id: 'knowledgePages.label.howToGuides.text'}),
      imageSrc: coupleWorking,
      href: getPageRoute('knowledge.howToGuides.home', locale),
    },
    {
      title: formatMessage({id: 'knowledgePages.label.customerStories.text'}),
      imageSrc: womanAtFrontDoor,
      href: getPageRoute('knowledge.customerStories.home', locale),
    },
  ];
  return (
    <GeneralLayout>
      <SEO title={category.title} />
      <KnowledgeHubHero selectedTag={category.slug} />
      <LinkColumnsWithTitle title={category.title} links={links} />
      <LayoutSection>
        <GrayLine />
        <LookingForFinance size={LookingForFinanceSize.LG} />
      </LayoutSection>
      <LayoutSection type={LayoutBackground.WHITE}>
        <RecommendedHubs hubs={hubs} />
      </LayoutSection>
    </GeneralLayout>
  );
}

export default KnowledgeCategoryPage;
